/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ViewEncapsulation, inject } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '@alfresco/adf-core';
import { DateInputComponent } from '@alfresco/aca-shared';
import { ExpiryService } from './expiry.service';

@Component({
  selector: 'app-set-expiry-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, DateInputComponent, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <h2 mat-dialog-title class="aca-admin-dialog__title"><mat-icon>event_busy</mat-icon> {{ 'DMS.UI.33BF8D' | translate }}</h2>
    <div mat-dialog-content class="aca-admin-dialog__body">
      <p class="aca-expiry-dialog__name">{{ name }}</p>
      <label class="aca-expiry-dialog__label">
        {{ 'DMS.UI.40E445' | translate }}
        <aca-date-input [(ngModel)]="dateValue" [min]="today" class="aca-expiry-dialog__date" />
      </label>
      <p class="aca-expiry-dialog__hint">{{ 'DMS.UI.CAB01C' | translate }}"{{ 'DMS.UI.DBCA12' | translate }}" {{ 'DMS.UI.38503E' | translate }}</p>
      <p class="aca-admin-dialog__error" *ngIf="error">{{ error }}</p>
    </div>
    <div mat-dialog-actions align="end" class="aca-admin-dialog__actions">
      <button mat-button (click)="clear()" [disabled]="saving" *ngIf="hadValue">{{ 'DMS.UI.759822' | translate }}</button>
      <span style="flex: 1"></span>
      <button mat-button (click)="close()">{{ 'DMS.UI.7D3094' | translate }}</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || !dateValue">
        {{ saving ? ('DMS.UI.2C6DE1' | translate) : ('DMS.UI.6EA45A' | translate) }}
      </button>
    </div>
  `
})
export class SetExpiryDialogComponent {
  private readonly expiry = inject(ExpiryService);
  private readonly notification = inject(NotificationService);
  private readonly ref = inject(MatDialogRef<SetExpiryDialogComponent>);
  private readonly data = inject<{ nodeId?: string; nodeIds?: string[]; name: string; current?: string }>(MAT_DIALOG_DATA);

  name = this.data?.name || '';
  today = new Date().toISOString().slice(0, 10);
  dateValue = this.data?.current ? this.data.current.slice(0, 10) : '';
  hadValue = !!this.data?.current;
  saving = false;
  error = '';

  save(): void {
    if (!this.dateValue) {
      return;
    }
    // store end-of-day so the whole day counts as still-valid
    const iso = new Date(this.dateValue + 'T23:59:59').toISOString();
    this.commit(iso, 'DMS.UI.5DC323');
  }

  clear(): void {
    this.commit(null, 'DMS.UI.683404');
  }

  private commit(iso: string | null, message: string): void {
    this.saving = true;
    this.error = '';
    // one document or a checked set — the same call, once per id
    const ids = this.data.nodeIds ?? [this.data.nodeId];
    const calls = ids.map((id) => this.expiry.setExpiry(id, iso).pipe(catchError(() => of(null))));
    const run = calls.length > 1 ? forkJoin(calls) : calls[0];
    run.subscribe({
      next: (res) => {
        const applied = Array.isArray(res) ? res.filter(Boolean).length : 1;
        const msg = ids.length > 1 ? `${message} (${applied}/${ids.length})` : message;
        if (applied) {
          this.notification.showInfo(msg);
          this.ref.close(true);
        } else {
          this.saving = false;
          this.error = 'DMS.UI.582420';
        }
      },
      error: (e) => {
        this.saving = false;
        this.error = e?.response?.body?.error?.briefSummary || e?.message || 'DMS.UI.582420';
      }
    });
  }

  close(): void {
    this.ref.close(false);
  }
}
